// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";
import { enCA } from "date-fns/locale";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Divider,
  Collapse,
  CardMedia,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartTextInput, SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalContentTextField,
  ModalTimePicker,
  ModalTimePickerSlotProps,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const CHART_ANALYZER_REGRESSION_GRADIENT_HELP_IMAGE = "/illustrations/converted/chart_analyzer/regression-gradient.png";
const CHART_ANALYZER_REGRESSION_GRADIENT_HELP_DESCRIPTION = (
  <>
    Let's delve into the theory behind calculating the regression gradient to comprehend the subsequent input parameters. At any given moment, the
    chart analyzer computes the regression line formed by all price points within the analyzed chart area. A regression line is a straight line that
    illustrates how prices have changed, with an equal number of data points above and below. <br />
    <br />
    Derived from the regression line, the regression gradient measures whether the prices analyzed within the chart analyzer boundaries trend upwards
    or downwards. The reference for this gradient is a horizontal line parallel to the upper and lower chart analyzer boundaries. Please refer to the
    image below to understand the values for which the input parameters <b>Regression gradient minimum</b> and <b>Regression gradient maximum</b>{" "}
    serve as the upper and lower limits.
    <br />
    <br />
    These gradient limit input parameters are interpreted as pitch rather than an angle value ranging from 0° to 90°. This interpretation allows for
    the reuse of this parameter across various assets. Hence, please interpret the term gradient as synonymous with pitch.
  </>
);

const CHART_ANALYZER_SWING_PHASE_HELP_IMAGE = "/illustrations/converted/chart_analyzer/swing-phase.png";
const CHART_ANALYZER_SWING_PHASE_HELP_DESCRIPTION = (
  <>
    A chart can typically be divided into distinct phases, each describing how the latest significant movement relates to preceding price action.
    <ul>
      <li>
        <b>Upswing:</b> The current swing has a positive slope, and the highest point is lower than the upper breakout defined by the previous highest
        point.
      </li>
      <li>
        <b>Downswing:</b> The current swing has a negative slope; the lowest point is higher than the lower breakout defined by the previous lowest
        point.
      </li>
      <li>
        <b>Overstatement:</b> The current swing has a positive slope, and the highest point is higher than the upper breakout defined by the previous
        highest point.
      </li>
      <li>
        <b>Understatement:</b> The current swing has a negative slope, and the lowest point is lower than the lower breakout defined by the previous
        lowest point.
      </li>
      <li>
        <b>Overstatement correction:</b> The current swing has a negative slope, and the lowest point is higher than the upper breakout defined by the
        previous highest point.
      </li>
      <li>
        <b>Understatement correction:</b> The current swing has a positive slope, and the highest point is lower than the lower breakout defined by
        the previous lowest point.
      </li>
    </ul>
    The chart analyzer continuously evaluates the current market swing and categorizes it into predefined phases. Using the on/off switches in this
    section, you can define which swing phases generate negative signals, thereby preventing the opening of new trades.
  </>
);

const CHART_ANALYZER_CURRENT_SWING_GRADIENT_HELP_IMAGE = "/illustrations/converted/chart_analyzer/current-swing-gradient.png";
const CHART_ANALYZER_CURRENT_SWING_GRADIENT_HELP_DESCRIPTION = (
  <>
    The current swing is a unique measure as it abstracts the most recent price movement based on height, width, and gradient. Typically, the first
    swing is considered the current swing. While previous swings may change retrospectively, the current swing updates in real time with each new
    tick.
    <br />
    <br />
    The input parameters <b>Current swing gradient minimum</b> and <b>Current swing gradient maximum</b> allow you to filter out periods when the
    gradient of the current swing exceeds these limits. As depicted below, the gradient of the current swing is calculated as the gradient between a
    parallel of the chart analyzer boundaries and the lines indicating the current swing. A negative value signifies a swing with a negative slope,
    while a positive value indicates a swing with a positive slope.
    <br />
    <br />
    These gradient limit input parameters are interpreted as pitch, not as an angle value ranging from 0° to 90°. This interpretation facilitates the
    reuse of this parameter across various assets. Therefore, please understand the term gradient as synonymous with pitch.
  </>
);

const CHART_ANALYZER_CURRENT_SWING_POSITION_HELP_IMAGE = "/illustrations/converted/chart_analyzer/current-swing-position.png";
const CHART_ANALYZER_CURRENT_SWING_POSITION_HELP_DESCRIPTION = (
  <>
    The vertical position of the rightmost point of the current swing, in relation to the upper and lower boundaries of the analyzer, is a valuable
    analysis parameter. It helps determine whether the current chart price trends towards support or resistance. This position is interpreted as a{" "}
    <b>value between 0.0 and 1.0</b>, where the upper boundary of the chart analyzer represents 1.0, and the lower boundary represents 0.0. The
    function principle is illustrated below.
    <br />
    <br />
    You can set an upper or lower threshold using the input parameters <b>Current swing position minimum</b> and <b>Current swing position maximum</b>
    . These parameters determine when the chart analyzer evaluates the current swing position as a positive trading signal. If the current position is
    outside these limits, trading is disabled.
  </>
);

const CHART_ANALYZER_SWING_NUMBER_HELP_IMAGE = "/illustrations/converted/chart_analyzer/swing-number.png";
const CHART_ANALYZER_SWING_NUMBER_HELP_DESCRIPTION = (
  <>
    All the input parameters discussed so far are utilized to adjust the chart analyzer and evaluate its basic analaysis parameters. However, the
    chart analyzer engine offers additional capabilities. The chart analyzer comprises instant evaluations and extended swing evaluations, also
    referred to as swing analysis. Typically, the swings within the chart analyzer area are numbered, <b>with the most recent swing labeled as one</b>
    , the second most recent as two, and so on. This principle is depicted in the illustration below.
    <br />
    <br />
    To enable the swing analysis for one or more swings, you need to specify which swings you want to observe in more detail using the input parameter{" "}
    <b>Swing number</b>. By default, the swing analysis is enabled, achieved by using the <b>Swing enabled</b> on/off switch. You can disabled this
    feature individually per number. You have the flexibility to analyze just one swing or up to 25.
    <br />
    <br />
    This feature enables visual chart interpretation and identification of complex patterns such as double tops or Fibonacci patterns. To generate a
    negative signal, use the input fields below to adjust the evaluation algorithm. By default, all swing analyses produce positive signals.
  </>
);

const CHART_ANALYZER_SWING_WIDTH_HEIGHT_HELP_IMAGE = "/illustrations/converted/chart_analyzer/swing-width-height.png";
const CHART_ANALYZER_SWING_WIDTH_HEIGHT_HELP_DESCRIPTION = (
  <>
    The chart analyzer continuously calculates the <b>height and width of a swing relative to its preceding swing</b>. For example, if the height is
    100%, the current swing results from a price movement of the same magnitude but in the opposite direction compared to the previous swing. This
    characteristic is illustrated in the diagram below.
    <br />
    <br />
    You can specify maximum and minimum values for the swing width and height to constrain the moments when the swing is considered part of a positive
    trading signal evaluation. By default, a swing is always evaluated as a positive trading signal. By restricting the range, you can accurately scan
    for specific patterns.
  </>
);

const CHART_ANALYZER_SWING_RANGE_WIDTH_RANGE_HEIGHT_HELP_IMAGE = "/illustrations/converted/chart_analyzer/swing-range-width-range-height.png";
const CHART_ANALYZER_SWING_RANGE_WIDTH_RANGE_HEIGHT_HELP_DESCRIPTION = (
  <>
    Another method of evaluating the swing height and width involves comparing them to the overall width and height of the chart analyzer. Similar to
    the current swing position, the upper boundary of the chart analyzer is interpreted as 1.0, and the lower boundary as 0.0. Additionally, the right
    edge of the chart analyzer area is interpreted as 0.0, and the left edge as 1.0. Using these relative measures, the{" "}
    <b>width and height of each swing can be represented by values ranging from 0.0 to 1.0</b>. This analysis method is entirely price- and
    time-agnostic and can be used to implement long-term stable signal generators.
    <br />
    <br />
    Like the previous section, four input parameters are available to define the maximum and minimum values for the height and width. All input
    parameters related to this feature are listed below.
  </>
);

const CHART_ANALYZER_SWING_POINT_POSITION_HELP_IMAGE = "/illustrations/converted/chart_analyzer/swing-point-position.png";
const CHART_ANALYZER_SWING_POINT_POSITION_HELP_HELP_DESCRIPTION = (
  <>
    The final set of input parameters can be used to accurately determine the position of the two points that define the straight swing line. The
    relative approach from the previous section applies here as well. The upper boundary of the chart analyzer is interpreted as 1.0, and the lower
    boundary as 0.0. Similarly, the right edge of the chart analyzer area is interpreted as 0.0 and the left edge as 1.0.{" "}
    <b>
      As shown in the function principle below, the two points can be interpreted as the range upper point (P1, y coordinate), range lower point (P2,
      y coordinate), range right point (P1, x coordinate), and range left point (P2, x coordinate).
    </b>{" "}
    <br />
    <br />
    For each point, you can define the minimum and maximum values it can have in relation to the chart analyzer area. The output signal of the swing
    analaysis is evaluated as positive only when the left and right points of the swing fall within the defined limits.
  </>
);

// ----------------------------------------------------------------------
// Smart Swing Number Input Component
// ----------------------------------------------------------------------
function SmartSwingNumberInput({ limits, value, onChange, error, helperText }) {
  const handleTextFieldChange = (event) => {
    const inputValue = event.target.value;

    onChange(inputValue);
  };

  const handleBlur = (event) => {
    const inputValue = event.target.value;

    // Format the value on blur if it's a valid number and digits are specified
    if (!Number.isNaN(parseFloat(inputValue)) && inputValue.trim() !== "") {
      const digits = limits?.digits;
      if (digits !== undefined) {
        const formattedValue = parseFloat(inputValue).toFixed(digits);
        onChange(formattedValue);
      }
    }
  };

  return (
    <ModalContentTextField
      value={value}
      onChange={handleTextFieldChange}
      onBlur={handleBlur}
      fullWidth
      size="small"
      type="number"
      inputProps={{
        step: limits?.step,
      }}
      error={error}
      InputLabelProps={{ shrink: true }}
    />
  );
}

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------
const MAX_SWING_CONFIGURATIONS = 25;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const ChartAnalyzersDefaults = {
  nSignalInUse: true,
  nSignalUsedFor: "0",
  nSignalVisualization: false,
  strSymbol: "Current",
  strExclusion: "00:00:00-00:00:00",
  nTimeframe: "-2",
  nShift: "0",
  //
  nWidth: "200",
  dProminence: "0.10",
  dBreakoutFactor: "1.00",
  //
  nMidVolaMinSwings: "6",
  dMidVolaMinROC: "0.00300000",
  dMidVolaMinFilled: "7.0",
  nHighVolaMinSwings: "9",
  dHighVolaMinROC: "0.00450000",
  dHighVolaMinFilled: "9.0",
  //
  nDrawBoundaries: true,
  nDrawExtrema: true,
  nDrawSupRes: true,
  nDrawSwingPhase: true,
  //
  strSupResForward: "ALL",
  dSupportMultiplier: "1.0",
  dResistanceMultiplier: "1.0",
  //
  dRegressionAnglesMin: "-25000000.0",
  dRegressionAnglesMax: "25000000.0",
  //
  nTradeVolatilityLow: true,
  nTradeVolatilityMiddle: true,
  nTradeVolatilityHigh: true,
  //
  nTradeSwingPhaseOverstatement: true,
  nTradeSwingPhaseUnderstatement: true,
  nTradeSwingPhaseOverCorrection: true,
  nTradeSwingPhaseUnderCorrection: true,
  nTradeSwingPhaseUpswing: true,
  nTradeSwingPhaseDownswing: true,
  nTradeSwingPhaseUndefined: true,
  //
  dCurrentSwingAngleMin: "-25000000.0",
  dCurrentSwingAngleMax: "25000000.0",
  //
  dCurrentSwingPosMin: "0.00",
  dCurrentSwingPosMax: "1.00",
  //
  nValueAnalysisFeedbackState: true,
  nSignalAnalysisFeedbackState: true,
};

export const ChartAnalyzersSwingConfigurationsDefaults = {
  bState: true,
  nNumber: "0",
  dHeightMax: "10000",
  dHeightMin: "-10000",
  dWidthMax: "10000",
  dWidthMin: "0",
  dRangeHeightMax: "1.00",
  dRangeHeightMin: "0.00",
  dRangeWidthMax: "1.00",
  dRangeWidthMin: "0.00",
  dRangeUpperPointMax: "1.00",
  dRangeUpperPointMin: "0.00",
  dRangeLowerPointMax: "1.00",
  dRangeLowerPointMin: "0.00",
  dRangeLeftPointMax: "1.00",
  dRangeLeftPointMin: "0.00",
  dRangeRightPointMax: "1.00",
  dRangeRightPointMin: "0.00",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  strSymbol: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
  nShift: {
    min: 0,
  },
  //
  nWidth: {
    min: 10,
  },
  dProminence: {
    digits: 2,
    step: 0.01,
    min: 0.0,
    max: 1.0,
  },
  dBreakoutFactor: {
    digits: 2,
    step: 0.1,
    min: 0.0,
  },
  //
  nMidVolaMinSwings: {
    min: 1,
    max: 100,
  },
  dMidVolaMinROC: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  dMidVolaMinFilled: {
    digits: 1,
    step: 0.1,
    min: 1.0,
    max: 100.0,
  },
  nHighVolaMinSwings: {
    min: 1,
    max: 100,
  },
  dHighVolaMinROC: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  dHighVolaMinFilled: {
    digits: 1,
    step: 0.1,
    min: 1.0,
    max: 100.0,
  },
  //
  strSupResForward: {
    maxLength: 50,
    emptyAllowed: false,
  },
  dSupportMultiplier: {
    digits: 1,
    step: 0.1,
    min: 0.1,
  },
  dResistanceMultiplier: {
    digits: 1,
    step: 0.1,
    min: 0.1,
  },
  //
  dRegressionAnglesMin: {
    digits: 1,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  dRegressionAnglesMax: {
    digits: 1,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  //
  dCurrentSwingAngleMin: {
    digits: 1,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  dCurrentSwingAngleMax: {
    digits: 1,
    step: 1,
    min: -25000000.0,
    max: 25000000.0,
  },
  //
  dCurrentSwingPosMin: {
    digits: 2,
    step: 0.01,
    min: 0.0,
    max: 1.0,
  },
  dCurrentSwingPosMax: {
    digits: 2,
    step: 0.01,
    min: 0.0,
    max: 1.0,
  },
  // Swing configuration number fields
  SWING_nNumber: {
    min: 1,
    max: 25,
  },
  SWING_dHeight: {
    min: -10000,
    max: 10000,
  },
  SWING_dWidth: {
    min: 0,
    max: 10000,
  },
  SWING_dRange: {
    digits: 2,
    step: 0.01,
    min: 0.0,
    max: 1.0,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const ChartAnalyzersBuildName = (config) => {
  const parts = [];

  // Add enabled status (similar to indicators)
  parts.push(`${config.nSignalInUse ? "On" : "Off"}`);

  // Add used for information
  const getUsedForDisplay = (value) => {
    const usedForOptions = {
      0: "Entry",
      1: "Exit",
      2: "Forward",
    };
    return usedForOptions[value] || "Entry";
  };
  parts.push(`${getUsedForDisplay(config.nSignalUsedFor)}`);

  // Add timeframe information
  const getTimeframeDisplay = (value) => {
    const timeframes = {
      "-2": "Current",
      0: "M1",
      1: "M5",
      2: "M15",
      3: "M30",
      4: "H1",
      5: "H4",
      6: "D1",
      7: "W1",
      8: "MN",
    };
    return timeframes[value] || "Current";
  };
  if (config.nTimeframe !== undefined) {
    parts.push(`${getTimeframeDisplay(config.nTimeframe)}`);
  }

  // Add symbol if different from default
  if (config.strSymbol && config.strSymbol !== "EURUSD") {
    parts.push(`${config.strSymbol}`);
  }

  return `Chart Analyzer (${parts.join("|")})`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const ChartAnalyzersBuildSubtitle = (config) => {
  // Helper function to get timeframe display
  const getTimeframeDisplay = (value) => {
    const timeframes = {
      "-2": "Current",
      0: "M1",
      1: "M5",
      2: "M15",
      3: "M30",
      4: "H1",
      5: "H4",
      6: "D1",
      7: "W1",
      8: "MN",
    };
    return timeframes[value] || "Current";
  };

  return `${config.strSymbol} | ${getTimeframeDisplay(config.nTimeframe)}`;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function ChartAnalyzersRenderParameters(selectedChartAnalyzer, getChartAnalyzerConfig, professionalMode) {
  const config = getChartAnalyzerConfig(selectedChartAnalyzer);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full", thin = false) => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: thin === false ? 600 : 300,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get switch display values (On/Off)
  const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

  // Helper function to get signal used for display
  const getSignalUsedForDisplay = (value) => {
    const usedForOptions = {
      0: "Entry",
      1: "Exit",
      2: "Forward",
    };
    return usedForOptions[value] || "Entry";
  };

  // Helper function to get timeframe display
  const getTimeframeDisplay = (value) => {
    const timeframes = {
      "-2": "Current",
      0: "M1",
      1: "M5",
      2: "M15",
      3: "M30",
      4: "H1",
      5: "H4",
      6: "D1",
      7: "W1",
      8: "MN",
    };
    return timeframes[value] || "Current";
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Signal in use", getSwitchDisplay(config.nSignalInUse))}
      {renderParam("Signal used for", getSignalUsedForDisplay(config.nSignalUsedFor))}
      {renderParam("Signal visualization", getSwitchDisplay(config.nSignalVisualization))}
      {renderParam("Symbol", config.strSymbol)}
      {professionalMode === true ? <>{renderParam("Excluded time range", config.strExclusion)}</> : null}
      {renderParam("Timeframe", getTimeframeDisplay(config.nTimeframe))}
      {renderParam("Shift", config.nShift)}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Analyzed width", config.nWidth)}
      {renderParam("Prominence", config.dProminence)}
      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("Medium volatility minimum swings", config.nMidVolaMinSwings)}
          {renderParam("Medium volatility minimum RoC", config.dMidVolaMinROC)}
          {renderParam("Medium volatility minimum filled", config.dMidVolaMinFilled)}
          {renderParam("High volatility minimum swings", config.nHighVolaMinSwings)}
          {renderParam("High volatility minimum RoC", config.dHighVolaMinROC)}
          {renderParam("High volatility minimum filled", config.dHighVolaMinFilled)}
          {renderParam("Signal if volatility is low", getSwitchDisplay(config.nTradeVolatilityLow))}
          {renderParam("Signal if volatility is middle", getSwitchDisplay(config.nTradeVolatilityMiddle))}
          {renderParam("Signal if volatility is high", getSwitchDisplay(config.nTradeVolatilityHigh))}
        </>
      ) : null}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Draw boundaries", getSwitchDisplay(config.nDrawBoundaries))}
      {renderParam("Draw extrema", getSwitchDisplay(config.nDrawExtrema))}
      {renderParam("Draw supports and resistances", getSwitchDisplay(config.nDrawSupRes))}
      {renderParam("Draw swing phase", getSwitchDisplay(config.nDrawSwingPhase))}
      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("Supports and resistances forward", config.strSupResForward)}
          {renderParam("Support multiplier", config.dSupportMultiplier)}
          {renderParam("Resistance multiplier", config.dResistanceMultiplier)}
        </>
      ) : null}

      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("Regression gradient minimum", config.dRegressionAnglesMin)}
          {renderParam("Regression gradient maximum", config.dRegressionAnglesMax)}
        </>
      ) : null}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Signal if swing phase is overstatement", getSwitchDisplay(config.nTradeSwingPhaseOverstatement))}
      {renderParam("Signal if swing phase is understatement", getSwitchDisplay(config.nTradeSwingPhaseUnderstatement))}
      {renderParam("Signal if swing phase is over correction", getSwitchDisplay(config.nTradeSwingPhaseOverCorrection))}
      {renderParam("Signal if swing phase is under correction", getSwitchDisplay(config.nTradeSwingPhaseUnderCorrection))}
      {renderParam("Signal if swing phase is upswing", getSwitchDisplay(config.nTradeSwingPhaseUpswing))}
      {renderParam("Signal if swing phase is downswing", getSwitchDisplay(config.nTradeSwingPhaseDownswing))}
      {renderParam("Signal if swing phase is undefined", getSwitchDisplay(config.nTradeSwingPhaseUndefined))}
      {renderParam("Breakout factor", config.dBreakoutFactor)}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Current swing gradient minimum", config.dCurrentSwingAngleMin)}
      {renderParam("Current swing gradient maximum", config.dCurrentSwingAngleMax)}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Current swing position minimum", config.dCurrentSwingPosMin)}
      {renderParam("Current swing position maximum", config.dCurrentSwingPosMax)}

      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("Value analysis feedback state", getSwitchDisplay(config.nValueAnalysisFeedbackState))}
          {renderParam("Signal analysis feedback state", getSwitchDisplay(config.nSignalAnalysisFeedbackState))}
        </>
      ) : null}

      {(() => {
        // Parse swing configurations
        let swingConfigs = [];

        // Check for new array format first
        if (config.swingConfigurations && Array.isArray(config.swingConfigurations)) {
          swingConfigs = config.swingConfigurations.map((swingData) => ({
            bState: swingData.bState !== undefined ? swingData.bState : ChartAnalyzersSwingConfigurationsDefaults.bState,
            nNumber: swingData.nNumber || ChartAnalyzersSwingConfigurationsDefaults.nNumber,
            heightMax: swingData.dHeightMax || ChartAnalyzersSwingConfigurationsDefaults.dHeightMax,
            heightMin: swingData.dHeightMin || ChartAnalyzersSwingConfigurationsDefaults.dHeightMin,
            widthMax: swingData.dWidthMax || ChartAnalyzersSwingConfigurationsDefaults.dWidthMax,
            widthMin: swingData.dWidthMin || ChartAnalyzersSwingConfigurationsDefaults.dWidthMin,
            rangeHeightMax: swingData.dRangeHeightMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMax,
            rangeHeightMin: swingData.dRangeHeightMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMin,
            rangeWidthMax: swingData.dRangeWidthMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMax,
            rangeWidthMin: swingData.dRangeWidthMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMin,
            rangeUpperPointMax: swingData.dRangeUpperPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMax,
            rangeUpperPointMin: swingData.dRangeUpperPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMin,
            rangeLowerPointMax: swingData.dRangeLowerPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMax,
            rangeLowerPointMin: swingData.dRangeLowerPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMin,
            rangeLeftPointMax: swingData.dRangeLeftPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMax,
            rangeLeftPointMin: swingData.dRangeLeftPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMin,
            rangeRightPointMax: swingData.dRangeRightPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMax,
            rangeRightPointMin: swingData.dRangeRightPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMin,
          }));
        }

        return swingConfigs.length > 0 ? (
          <>
            <Box>
              <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
            </Box>
            <Typography variant="contentTiny" sx={{ fontWeight: 600 }}>
              Swing configurations ({swingConfigs.length}/{MAX_SWING_CONFIGURATIONS})
            </Typography>
            {swingConfigs.map((swing, index) => (
              <Stack key={index} spacing={0.25} sx={{ mb: 1 }}>
                <Typography variant="contentTiny" sx={{ fontWeight: 600 }}>
                  Swing #{swing.nNumber}
                </Typography>
                {renderParam("Swing enabled", getSwitchDisplay(swing.bState), "auto", "full", true)}
                {renderParam("Height minimum", swing.heightMin, "auto", "full", true)}
                {renderParam("Height maximum", swing.heightMax, "auto", "full", true)}
                {renderParam("Width minimum", swing.widthMin, "auto", "full", true)}
                {renderParam("Width maximum", swing.widthMax, "auto", "full", true)}
                {renderParam("Range hight minimum", swing.rangeHeightMin, "auto", "full", true)}
                {renderParam("Range height maximum", swing.rangeHeightMax, "auto", "full", true)}
                {renderParam("Range width minimum", swing.rangeWidthMin, "auto", "full", true)}
                {renderParam("Range width maximum", swing.rangeWidthMax, "auto", "full", true)}
                {renderParam("Range upper point minimum", swing.rangeUpperPointMin, "auto", "full", true)}
                {renderParam("Range upper point maximum", swing.rangeUpperPointMax, "auto", "full", true)}
                {renderParam("Range lower point minimum", swing.rangeLowerPointMin, "auto", "full", true)}
                {renderParam("Range lower point maximum", swing.rangeLowerPointMax, "auto", "full", true)}
                {renderParam("Range left point minimum", swing.rangeLeftPointMin, "auto", "full", true)}
                {renderParam("Range left point maximum", swing.rangeLeftPointMax, "auto", "full", true)}
                {renderParam("Range right point minimum", swing.rangeRightPointMin, "auto", "full", true)}
                {renderParam("Range right point maximum", swing.rangeRightPointMax, "auto", "full", true)}
              </Stack>
            ))}
          </>
        ) : null;
      })()}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const ChartAnalyzersConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  ChartAnalyzersConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [collapseSwingPhase, setCollapseSwingPhase] = useState(false);
  const [collapseRegressionGradient, setCollapseRegressionGradient] = useState(false);
  const [collapseCurrentSwingGradient, setCollapseCurrentSwingGradient] = useState(false);
  const [collapseCurrentSwingPosition, setCollapseCurrentSwingPosition] = useState(false);
  const [collapseSwingNumber, setCollapseSwingNumber] = useState(false);
  const [collapseSwingWidthHeight, setCollapseSwingWidthHeight] = useState(false);
  const [collapseSwingRangeWidthRangeHeight, setCollapseSwingRangeWidthRangeHeight] = useState(false);
  const [collapseSwingPointPosition, setCollapseSwingPointPosition] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getSwingValidationErrors = () => {
    const allErrors = {};
    swingConfigurations.forEach((swing) => {
      const errors = validateSwing(swing);
      if (Object.keys(errors).length > 0) {
        allErrors[swing.id] = errors;
      }
    });
    return allErrors;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nSignalInUse: initialData?.nSignalInUse ?? ChartAnalyzersDefaults.nSignalInUse,
      nSignalUsedFor: initialData?.nSignalUsedFor ?? ChartAnalyzersDefaults.nSignalUsedFor,
      nSignalVisualization: initialData?.nSignalVisualization ?? ChartAnalyzersDefaults.nSignalVisualization,
      strSymbol: initialData?.strSymbol ?? ChartAnalyzersDefaults.strSymbol,
      strExclusion: initialData?.strExclusion ?? ChartAnalyzersDefaults.strExclusion,
      nTimeframe: initialData?.nTimeframe ?? ChartAnalyzersDefaults.nTimeframe,
      nShift: initialData?.nShift ?? ChartAnalyzersDefaults.nShift,
      //
      nWidth: initialData?.nWidth ?? ChartAnalyzersDefaults.nWidth,
      dProminence: initialData?.dProminence ?? ChartAnalyzersDefaults.dProminence,
      dBreakoutFactor: initialData?.dBreakoutFactor ?? ChartAnalyzersDefaults.dBreakoutFactor,
      //
      nMidVolaMinSwings: initialData?.nMidVolaMinSwings ?? ChartAnalyzersDefaults.nMidVolaMinSwings,
      dMidVolaMinROC: initialData?.dMidVolaMinROC ?? ChartAnalyzersDefaults.dMidVolaMinROC,
      dMidVolaMinFilled: initialData?.dMidVolaMinFilled ?? ChartAnalyzersDefaults.dMidVolaMinFilled,
      nHighVolaMinSwings: initialData?.nHighVolaMinSwings ?? ChartAnalyzersDefaults.nHighVolaMinSwings,
      dHighVolaMinROC: initialData?.dHighVolaMinROC ?? ChartAnalyzersDefaults.dHighVolaMinROC,
      dHighVolaMinFilled: initialData?.dHighVolaMinFilled ?? ChartAnalyzersDefaults.dHighVolaMinFilled,
      //
      nDrawBoundaries: initialData?.nDrawBoundaries ?? ChartAnalyzersDefaults.nDrawBoundaries,
      nDrawExtrema: initialData?.nDrawExtrema ?? ChartAnalyzersDefaults.nDrawExtrema,
      nDrawSupRes: initialData?.nDrawSupRes ?? ChartAnalyzersDefaults.nDrawSupRes,
      nDrawSwingPhase: initialData?.nDrawSwingPhase ?? ChartAnalyzersDefaults.nDrawSwingPhase,
      //
      strSupResForward: initialData?.strSupResForward ?? ChartAnalyzersDefaults.strSupResForward,
      dSupportMultiplier: initialData?.dSupportMultiplier ?? ChartAnalyzersDefaults.dSupportMultiplier,
      dResistanceMultiplier: initialData?.dResistanceMultiplier ?? ChartAnalyzersDefaults.dResistanceMultiplier,
      //
      dRegressionAnglesMin: initialData?.dRegressionAnglesMin ?? ChartAnalyzersDefaults.dRegressionAnglesMin,
      dRegressionAnglesMax: initialData?.dRegressionAnglesMax ?? ChartAnalyzersDefaults.dRegressionAnglesMax,
      //
      nTradeVolatilityLow: initialData?.nTradeVolatilityLow ?? ChartAnalyzersDefaults.nTradeVolatilityLow,
      nTradeVolatilityMiddle: initialData?.nTradeVolatilityMiddle ?? ChartAnalyzersDefaults.nTradeVolatilityMiddle,
      nTradeVolatilityHigh: initialData?.nTradeVolatilityHigh ?? ChartAnalyzersDefaults.nTradeVolatilityHigh,
      //
      nTradeSwingPhaseOverstatement: initialData?.nTradeSwingPhaseOverstatement ?? ChartAnalyzersDefaults.nTradeSwingPhaseOverstatement,
      nTradeSwingPhaseUnderstatement: initialData?.nTradeSwingPhaseUnderstatement ?? ChartAnalyzersDefaults.nTradeSwingPhaseUnderstatement,
      nTradeSwingPhaseOverCorrection: initialData?.nTradeSwingPhaseOverCorrection ?? ChartAnalyzersDefaults.nTradeSwingPhaseOverCorrection,
      nTradeSwingPhaseUnderCorrection: initialData?.nTradeSwingPhaseUnderCorrection ?? ChartAnalyzersDefaults.nTradeSwingPhaseUnderCorrection,
      nTradeSwingPhaseUpswing: initialData?.nTradeSwingPhaseUpswing ?? ChartAnalyzersDefaults.nTradeSwingPhaseUpswing,
      nTradeSwingPhaseDownswing: initialData?.nTradeSwingPhaseDownswing ?? ChartAnalyzersDefaults.nTradeSwingPhaseDownswing,
      nTradeSwingPhaseUndefined: initialData?.nTradeSwingPhaseUndefined ?? ChartAnalyzersDefaults.nTradeSwingPhaseUndefined,
      //
      dCurrentSwingAngleMin: initialData?.dCurrentSwingAngleMin ?? ChartAnalyzersDefaults.dCurrentSwingAngleMin,
      dCurrentSwingAngleMax: initialData?.dCurrentSwingAngleMax ?? ChartAnalyzersDefaults.dCurrentSwingAngleMax,
      //
      dCurrentSwingPosMin: initialData?.dCurrentSwingPosMin ?? ChartAnalyzersDefaults.dCurrentSwingPosMin,
      dCurrentSwingPosMax: initialData?.dCurrentSwingPosMax ?? ChartAnalyzersDefaults.dCurrentSwingPosMax,
      //
      nValueAnalysisFeedbackState: initialData?.nValueAnalysisFeedbackState ?? ChartAnalyzersDefaults.nValueAnalysisFeedbackState,
      nSignalAnalysisFeedbackState: initialData?.nSignalAnalysisFeedbackState ?? ChartAnalyzersDefaults.nSignalAnalysisFeedbackState,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          // Check for validation errors in swing configurations
          const validationErrors = getSwingValidationErrors();
          if (Object.keys(validationErrors).length > 0) {
            const errorMessages = [];
            Object.values(validationErrors).forEach((errors) => {
              Object.values(errors).forEach((error) => {
                if (!errorMessages.includes(error)) {
                  errorMessages.push(error);
                }
              });
            });

            setError(true);
            setReturnMessage(`Validation errors: ${errorMessages.join(", ")}`);
            setSubmitting(false);

            // Clear error message after 5 seconds
            setTimeout(() => {
              setError(false);
              setReturnMessage("");
            }, 5000);

            return;
          }

          // Format swing configurations as array - only include configurations with valid swing numbers
          const formattedValues = { ...values };
          const validSwingConfigs = swingConfigurations.filter((swing) => parseInt(swing.nNumber, 10) >= 1 && parseInt(swing.nNumber, 10) <= 25);

          // Create swing configurations array
          formattedValues.swingConfigurations = validSwingConfigs.map((swing) => ({
            bState: swing.bState,
            nNumber: swing.nNumber,
            dHeightMax: swing.heightMax,
            dHeightMin: swing.heightMin,
            dWidthMax: swing.widthMax,
            dWidthMin: swing.widthMin,
            dRangeHeightMax: swing.rangeHeightMax,
            dRangeHeightMin: swing.rangeHeightMin,
            dRangeWidthMax: swing.rangeWidthMax,
            dRangeWidthMin: swing.rangeWidthMin,
            dRangeUpperPointMax: swing.rangeUpperPointMax,
            dRangeUpperPointMin: swing.rangeUpperPointMin,
            dRangeLowerPointMax: swing.rangeLowerPointMax,
            dRangeLowerPointMin: swing.rangeLowerPointMin,
            dRangeLeftPointMax: swing.rangeLeftPointMax,
            dRangeLeftPointMin: swing.rangeLeftPointMin,
            dRangeRightPointMax: swing.rangeRightPointMax,
            dRangeRightPointMin: swing.rangeRightPointMin,
          }));

          onSave(formattedValues);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // Parse initial swing configurations from initialData
  const parseInitialSwingConfigs = () => {
    const swingConfigs = [];
    if (initialData) {
      // Check for new array format first
      if (initialData.swingConfigurations && Array.isArray(initialData.swingConfigurations)) {
        initialData.swingConfigurations.forEach((swingData, index) => {
          swingConfigs.push({
            id: index + 1,
            nNumber: swingData.nNumber || "1",
            bState: swingData.bState !== undefined ? swingData.bState : ChartAnalyzersSwingConfigurationsDefaults.bState,
            heightMax: swingData.dHeightMax || ChartAnalyzersSwingConfigurationsDefaults.dHeightMax,
            heightMin: swingData.dHeightMin || ChartAnalyzersSwingConfigurationsDefaults.dHeightMin,
            widthMax: swingData.dWidthMax || ChartAnalyzersSwingConfigurationsDefaults.dWidthMax,
            widthMin: swingData.dWidthMin || ChartAnalyzersSwingConfigurationsDefaults.dWidthMin,
            rangeHeightMax: swingData.dRangeHeightMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMax,
            rangeHeightMin: swingData.dRangeHeightMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMin,
            rangeWidthMax: swingData.dRangeWidthMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMax,
            rangeWidthMin: swingData.dRangeWidthMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMin,
            rangeUpperPointMax: swingData.dRangeUpperPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMax,
            rangeUpperPointMin: swingData.dRangeUpperPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMin,
            rangeLowerPointMax: swingData.dRangeLowerPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMax,
            rangeLowerPointMin: swingData.dRangeLowerPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMin,
            rangeLeftPointMax: swingData.dRangeLeftPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMax,
            rangeLeftPointMin: swingData.dRangeLeftPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMin,
            rangeRightPointMax: swingData.dRangeRightPointMax || ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMax,
            rangeRightPointMin: swingData.dRangeRightPointMin || ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMin,
          });
        });
      }
    }
    return swingConfigs.length > 0 ? swingConfigs : [];
  };

  const [swingConfigurations, setSwingConfigurations] = useState(parseInitialSwingConfigs);

  // Update swing configurations when initialData changes (e.g., switching between chart analyzers)
  useEffect(() => {
    setSwingConfigurations(parseInitialSwingConfigs());
  }, [initialData]);

  const addNewSwingConfig = () => {
    if (swingConfigurations.length < MAX_SWING_CONFIGURATIONS) {
      const newId = Math.max(...swingConfigurations.map((s) => s.id), 0) + 1;
      // Find the next available swing number (1-25)
      // Convert string numbers to integers for comparison
      const existingNumbers = swingConfigurations.map((s) => parseInt(s.nNumber, 10));
      let nextNumber = 1;
      while (nextNumber <= 25 && existingNumbers.includes(nextNumber)) {
        nextNumber += 1;
      }
      // If all numbers 1-25 are taken, default to 1 (user can change it)
      if (nextNumber > 25) nextNumber = 1;

      setSwingConfigurations([
        ...swingConfigurations,
        {
          id: newId,
          nNumber: nextNumber.toString(),
          bState: ChartAnalyzersSwingConfigurationsDefaults.bState,
          heightMax: ChartAnalyzersSwingConfigurationsDefaults.dHeightMax,
          heightMin: ChartAnalyzersSwingConfigurationsDefaults.dHeightMin,
          widthMax: ChartAnalyzersSwingConfigurationsDefaults.dWidthMax,
          widthMin: ChartAnalyzersSwingConfigurationsDefaults.dWidthMin,
          rangeHeightMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMax,
          rangeHeightMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMin,
          rangeWidthMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMax,
          rangeWidthMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMin,
          rangeUpperPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMax,
          rangeUpperPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMin,
          rangeLowerPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMax,
          rangeLowerPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMin,
          rangeLeftPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMax,
          rangeLeftPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMin,
          rangeRightPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMax,
          rangeRightPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMin,
        },
      ]);
    }
  };

  const removeSwingConfig = (id) => {
    setSwingConfigurations(swingConfigurations.filter((swing) => swing.id !== id));
  };

  const updateSwingConfig = (id, field, value) => {
    setSwingConfigurations(swingConfigurations.map((swing) => (swing.id === id ? { ...swing, [field]: value } : swing)));
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Clear cross-validation error state
    setError(false);
    setReturnMessage("");
    // Apply all the default values to the form
    Object.keys(ChartAnalyzersDefaults).forEach((key) => {
      setFieldValue(key, ChartAnalyzersDefaults[key]);
    });

    // Reset swing configurations values but preserve the existing configurations
    const resetSwingConfigurations = swingConfigurations.map((swing) => ({
      ...swing,
      // Reset all swing values to defaults while preserving id and nNumber
      bState: ChartAnalyzersSwingConfigurationsDefaults.bState,
      heightMax: ChartAnalyzersSwingConfigurationsDefaults.dHeightMax,
      heightMin: ChartAnalyzersSwingConfigurationsDefaults.dHeightMin,
      widthMax: ChartAnalyzersSwingConfigurationsDefaults.dWidthMax,
      widthMin: ChartAnalyzersSwingConfigurationsDefaults.dWidthMin,
      rangeHeightMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMax,
      rangeHeightMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeHeightMin,
      rangeWidthMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMax,
      rangeWidthMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeWidthMin,
      rangeUpperPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMax,
      rangeUpperPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeUpperPointMin,
      rangeLowerPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMax,
      rangeLowerPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeLowerPointMin,
      rangeLeftPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMax,
      rangeLeftPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeLeftPointMin,
      rangeRightPointMax: ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMax,
      rangeRightPointMin: ChartAnalyzersSwingConfigurationsDefaults.dRangeRightPointMin,
    }));

    setSwingConfigurations(resetSwingConfigurations);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const parseExclusionString = (exclusionString) => {
    if (!exclusionString || exclusionString === "00:00:00-00:00:00") {
      return {
        start: dayjs().hour(0).minute(0).second(0),
        end: dayjs().hour(0).minute(0).second(0),
      };
    }

    const [startStr, endStr] = exclusionString.split("-");
    const [startHour, startMin, startSec] = startStr.split(":").map(Number);
    const [endHour, endMin, endSec] = endStr.split(":").map(Number);

    return {
      start: dayjs().hour(startHour).minute(startMin).second(startSec),
      end: dayjs().hour(endHour).minute(endMin).second(endSec),
    };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formatExclusionString = (start, end) => {
    const startStr = start.format("HH:mm:ss");
    const endStr = end.format("HH:mm:ss");
    return `${startStr}-${endStr}`;
  };

  // ----------------------------------------------------------------------
  // Cross-validation logic
  // ----------------------------------------------------------------------
  useEffect(() => {
    const nMidVolaMinSwings = parseFloat(values.nMidVolaMinSwings);
    const nHighVolaMinSwings = parseFloat(values.nHighVolaMinSwings);
    const dMidVolaMinROC = parseFloat(values.dMidVolaMinROC);
    const dHighVolaMinROC = parseFloat(values.dHighVolaMinROC);
    const dMidVolaMinFilled = parseFloat(values.dMidVolaMinFilled);
    const dHighVolaMinFilled = parseFloat(values.dHighVolaMinFilled);
    const dRegressionAnglesMin = parseFloat(values.dRegressionAnglesMin);
    const dRegressionAnglesMax = parseFloat(values.dRegressionAnglesMax);
    const dCurrentSwingAngleMin = parseFloat(values.dCurrentSwingAngleMin);
    const dCurrentSwingAngleMax = parseFloat(values.dCurrentSwingAngleMax);
    const dCurrentSwingPosMin = parseFloat(values.dCurrentSwingPosMin);
    const dCurrentSwingPosMax = parseFloat(values.dCurrentSwingPosMax);

    // Check Medium vs High volatility minimum swings
    if (!Number.isNaN(nMidVolaMinSwings) && !Number.isNaN(nHighVolaMinSwings)) {
      if (nMidVolaMinSwings >= nHighVolaMinSwings) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Medium volatility minimum swings</b> cannot be greater or equal to <b>High volatility minimum swings</b>!
          </>
        );
        return;
      }
    }

    // Check Medium vs High volatility minimum RoC
    if (!Number.isNaN(dMidVolaMinROC) && !Number.isNaN(dHighVolaMinROC)) {
      if (dMidVolaMinROC >= dHighVolaMinROC) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Medium volatility minimum RoC</b> cannot be greater or equal to <b>High volatility minimum RoC</b>!
          </>
        );
        return;
      }
    }

    // Check Medium vs High volatility minimum filled
    if (!Number.isNaN(dMidVolaMinFilled) && !Number.isNaN(dHighVolaMinFilled)) {
      if (dMidVolaMinFilled >= dHighVolaMinFilled) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Medium volatility minimum filled</b> cannot be greater or equal to <b>High volatility minimum filled</b>!
          </>
        );
        return;
      }
    }

    // Check Regression gradient min vs max
    if (!Number.isNaN(dRegressionAnglesMin) && !Number.isNaN(dRegressionAnglesMax)) {
      if (dRegressionAnglesMin >= dRegressionAnglesMax) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Regression gradient minimum</b> cannot be greater or equal to <b>Regression gradient maximum</b>!
          </>
        );
        return;
      }
    }

    // Check Current swing gradient min vs max
    if (!Number.isNaN(dCurrentSwingAngleMin) && !Number.isNaN(dCurrentSwingAngleMax)) {
      if (dCurrentSwingAngleMin >= dCurrentSwingAngleMax) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Current swing gradient minimum</b> cannot be greater or equal to <b>Current swing gradient maximum</b>!
          </>
        );
        return;
      }
    }

    // Check Current swing position min vs max
    if (!Number.isNaN(dCurrentSwingPosMin) && !Number.isNaN(dCurrentSwingPosMax)) {
      if (dCurrentSwingPosMin >= dCurrentSwingPosMax) {
        setError(true);
        setSuccess(false);
        setReturnMessage(
          <>
            <b>Current swing position minimum</b> cannot be greater or equal to <b>Current swing position maximum</b>!
          </>
        );
        return;
      }
    }

    // Check swing configurations cross-validation
    const swingValidationError = swingConfigurations.find((swing) => {
      const heightMin = parseFloat(swing.heightMin);
      const heightMax = parseFloat(swing.heightMax);
      const widthMin = parseFloat(swing.widthMin);
      const widthMax = parseFloat(swing.widthMax);
      const rangeHeightMin = parseFloat(swing.rangeHeightMin);
      const rangeHeightMax = parseFloat(swing.rangeHeightMax);
      const rangeWidthMin = parseFloat(swing.rangeWidthMin);
      const rangeWidthMax = parseFloat(swing.rangeWidthMax);
      const rangeUpperPointMin = parseFloat(swing.rangeUpperPointMin);
      const rangeUpperPointMax = parseFloat(swing.rangeUpperPointMax);
      const rangeLowerPointMin = parseFloat(swing.rangeLowerPointMin);
      const rangeLowerPointMax = parseFloat(swing.rangeLowerPointMax);
      const rangeLeftPointMin = parseFloat(swing.rangeLeftPointMin);
      const rangeLeftPointMax = parseFloat(swing.rangeLeftPointMax);
      const rangeRightPointMin = parseFloat(swing.rangeRightPointMin);
      const rangeRightPointMax = parseFloat(swing.rangeRightPointMax);

      // Height validation
      if (!Number.isNaN(heightMin) && !Number.isNaN(heightMax)) {
        if (heightMax <= heightMin) {
          swing.errorMessage = (
            <>
              <b>Height maximum</b> cannot be smaller or equal to <b>Height minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Width validation
      if (!Number.isNaN(widthMin) && !Number.isNaN(widthMax)) {
        if (widthMax <= widthMin) {
          swing.errorMessage = (
            <>
              <b>Width maximum</b> cannot be smaller or equal to <b>Width minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range height validation
      if (!Number.isNaN(rangeHeightMin) && !Number.isNaN(rangeHeightMax)) {
        if (rangeHeightMax <= rangeHeightMin) {
          swing.errorMessage = (
            <>
              <b>Range height maximum</b> cannot be smaller or equal to <b>Range height minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range width validation
      if (!Number.isNaN(rangeWidthMin) && !Number.isNaN(rangeWidthMax)) {
        if (rangeWidthMax <= rangeWidthMin) {
          swing.errorMessage = (
            <>
              <b>Range width maximum</b> cannot be smaller or equal to <b>Range width minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range upper point validation
      if (!Number.isNaN(rangeUpperPointMin) && !Number.isNaN(rangeUpperPointMax)) {
        if (rangeUpperPointMax <= rangeUpperPointMin) {
          swing.errorMessage = (
            <>
              <b>Range upper point maximum</b> cannot be smaller or equal to <b>Range upper point minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range lower point validation
      if (!Number.isNaN(rangeLowerPointMin) && !Number.isNaN(rangeLowerPointMax)) {
        if (rangeLowerPointMax <= rangeLowerPointMin) {
          swing.errorMessage = (
            <>
              <b>Range lower point maximum</b> cannot be smaller or equal to <b>Range lower point minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range left point validation
      if (!Number.isNaN(rangeLeftPointMin) && !Number.isNaN(rangeLeftPointMax)) {
        if (rangeLeftPointMax <= rangeLeftPointMin) {
          swing.errorMessage = (
            <>
              <b>Range left point maximum</b> cannot be smaller or equal to <b>Range left point minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      // Range right point validation
      if (!Number.isNaN(rangeRightPointMin) && !Number.isNaN(rangeRightPointMax)) {
        if (rangeRightPointMax <= rangeRightPointMin) {
          swing.errorMessage = (
            <>
              <b>Range right point maximum</b> cannot be smaller or equal to <b>Range right point minimum</b> in swing {swing.nNumber}!
            </>
          );
          return true;
        }
      }

      return false;
    });

    if (swingValidationError) {
      setError(true);
      setSuccess(false);
      setReturnMessage(swingValidationError.errorMessage);
      return;
    }

    // If all validations pass, clear error state
    setError(false);
  }, [
    values.nMidVolaMinSwings,
    values.nHighVolaMinSwings,
    values.dMidVolaMinROC,
    values.dHighVolaMinROC,
    values.dMidVolaMinFilled,
    values.dHighVolaMinFilled,
    values.dRegressionAnglesMin,
    values.dRegressionAnglesMax,
    values.dCurrentSwingAngleMin,
    values.dCurrentSwingAngleMax,
    values.dCurrentSwingPosMin,
    values.dCurrentSwingPosMax,
    swingConfigurations,
  ]);

  // Parse exclusion times for the time pickers
  const exclusionTimes = parseExclusionString(values.strExclusion);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Validation Functions
  // ----------------------------------------------------------------------
  const validateSwingField = (value, limits, fieldName) => {
    if (!value || value.trim() === "") {
      return "Field cannot be empty.";
    }

    const numValue = parseFloat(value);
    if (Number.isNaN(numValue)) {
      return "Invalid number format.";
    }

    if (limits?.min !== undefined && numValue < limits.min) {
      return `Value cannot be below ${limits.min}`;
    }

    if (limits?.max !== undefined && numValue > limits.max) {
      return `Value cannot be above ${limits.max}`;
    }

    return null;
  };

  const validateSwing = (swing) => {
    const errors = {};

    // Validate swing number
    const nNumberError = validateSwingField(swing.nNumber, DEFAULT_LIMITS.SWING_nNumber, "nNumber");
    if (nNumberError) errors.nNumber = nNumberError;

    // Validate height fields
    const heightMaxError = validateSwingField(swing.heightMax, DEFAULT_LIMITS.SWING_dHeight, "heightMax");
    if (heightMaxError) errors.heightMax = heightMaxError;

    const heightMinError = validateSwingField(swing.heightMin, DEFAULT_LIMITS.SWING_dHeight, "heightMin");
    if (heightMinError) errors.heightMin = heightMinError;

    // Validate width fields
    const widthMaxError = validateSwingField(swing.widthMax, DEFAULT_LIMITS.SWING_dWidth, "widthMax");
    if (widthMaxError) errors.widthMax = widthMaxError;

    const widthMinError = validateSwingField(swing.widthMin, DEFAULT_LIMITS.SWING_dWidth, "widthMin");
    if (widthMinError) errors.widthMin = widthMinError;

    // Validate range fields
    const rangeFields = [
      "rangeHeightMax",
      "rangeHeightMin",
      "rangeWidthMax",
      "rangeWidthMin",
      "rangeUpperPointMax",
      "rangeUpperPointMin",
      "rangeLowerPointMax",
      "rangeLowerPointMin",
      "rangeLeftPointMax",
      "rangeLeftPointMin",
      "rangeRightPointMax",
      "rangeRightPointMin",
    ];

    rangeFields.forEach((field) => {
      const error = validateSwingField(swing[field], DEFAULT_LIMITS.SWING_dRange, field);
      if (error) errors[field] = error;
    });

    return errors;
  };

  // Create emulated formik structure for SmartParameter error display
  const createSwingFormikStructure = () => {
    const swingErrors = getSwingValidationErrors();
    const emulatedErrors = {};
    const emulatedTouched = {};

    // Create field names and populate errors/touched for each swing
    swingConfigurations.forEach((swing) => {
      const fields = [
        "nNumber",
        "heightMax",
        "heightMin",
        "widthMax",
        "widthMin",
        "rangeHeightMax",
        "rangeHeightMin",
        "rangeWidthMax",
        "rangeWidthMin",
        "rangeUpperPointMax",
        "rangeUpperPointMin",
        "rangeLowerPointMax",
        "rangeLowerPointMin",
        "rangeLeftPointMax",
        "rangeLeftPointMin",
        "rangeRightPointMax",
        "rangeRightPointMin",
      ];

      fields.forEach((field) => {
        const fieldName = `swing_${swing.id}_${field}`;

        // Set touched to true (since user has interacted with these fields)
        emulatedTouched[fieldName] = true;

        // Set errors if they exist
        if (swingErrors[swing.id]?.[field]) {
          emulatedErrors[fieldName] = swingErrors[swing.id][field];
        }
      });
    });

    return { errors: emulatedErrors, touched: emulatedTouched };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;
  const swingErrors = getSwingValidationErrors();
  const hasSwingErrors = Object.keys(swingErrors).length > 0;
  const swingFormikStructure = createSwingFormikStructure();

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors() || hasSwingErrors) {
    saveAndCloseDisabled = true;
  }

  // Disable save button if cross-validation fails
  if (error) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure chart analyzers
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure chart analyzers</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalInUse */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal in use"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalInUse"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalInUse}
                        onChange={(event) => {
                          setFieldValue("nSignalInUse", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalUsedFor */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal used for"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalUsedFor"
                        touched={touched}
                        errors={errors}
                      />
                      {initialData === null || !initialData.forwardUsed ? (
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nSignalUsedFor")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nSignalUsedFor && errors.nSignalUsedFor)}
                          >
                            <ModalContentMenuItem value="0">Entry</ModalContentMenuItem>
                            <ModalContentMenuItem value="1">Exit</ModalContentMenuItem>
                            <ModalContentMenuItem value="2">Forward</ModalContentMenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="contentSmall" sx={{ fontWeight: "bold" }}>
                          This chart analyzer is linked to another component that relies on its value, so it cannot be modified. To make changes,
                          please release the signal in the associated component first.
                        </Typography>
                      )}
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nSignalVisualization */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal visualization"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nSignalVisualization"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nSignalVisualization}
                        onChange={(event) => {
                          setFieldValue("nSignalVisualization", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* strSymbol */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Symbol"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="strSymbol"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartTextInput textFieldName="strSymbol" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strExclusion */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter
                        value="Excluded time range"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="strExclusion"
                        touched={touched}
                        errors={errors}
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ModalTimePicker
                            value={exclusionTimes.start}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(value, exclusionTimes.end);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                          <Typography variant="contentTiny">-</Typography>
                          <ModalTimePicker
                            value={exclusionTimes.end}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(exclusionTimes.start, value);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTimeframe */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Timeframe"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTimeframe"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nTimeframe")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nTimeframe && errors.nTimeframe)}
                        >
                          <ModalContentMenuItem value="-2">Current</ModalContentMenuItem>
                          <ModalContentMenuItem value="0">M1</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">M5</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">M15</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">M30</ModalContentMenuItem>
                          <ModalContentMenuItem value="4">H1</ModalContentMenuItem>
                          <ModalContentMenuItem value="5">H4</ModalContentMenuItem>
                          <ModalContentMenuItem value="6">D1</ModalContentMenuItem>
                          <ModalContentMenuItem value="7">W1</ModalContentMenuItem>
                          <ModalContentMenuItem value="8">MN</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nShift */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Shift"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nShift"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nShift" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nWidth */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Analyzed width"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nWidth"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nWidth" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dProminence */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Prominence"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="dProminence"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="dProminence" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nMidVolaMinSwings */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Medium volatility minimum swings"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="nMidVolaMinSwings"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="nMidVolaMinSwings" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dMidVolaMinROC */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Medium volatility minimum RoC"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dMidVolaMinROC"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dMidVolaMinROC" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dMidVolaMinFilled */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Medium volatility minimum filled"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dMidVolaMinFilled"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dMidVolaMinFilled" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nHighVolaMinSwings */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="High volatility minimum swings"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="nHighVolaMinSwings"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="nHighVolaMinSwings" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dHighVolaMinROC */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="High volatility minimum RoC"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dHighVolaMinROC"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dHighVolaMinROC" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dHighVolaMinFilled */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="High volatility minimum filled"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dHighVolaMinFilled"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dHighVolaMinFilled" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeVolatilityLow */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if volatility is low"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeVolatilityLow"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeVolatilityLow}
                        onChange={(event) => {
                          setFieldValue("nTradeVolatilityLow", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeVolatilityMiddle */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if volatility is middle"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeVolatilityMiddle"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeVolatilityMiddle}
                        onChange={(event) => {
                          setFieldValue("nTradeVolatilityMiddle", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeVolatilityHigh */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if volatility is high"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeVolatilityHigh"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeVolatilityHigh}
                        onChange={(event) => {
                          setFieldValue("nTradeVolatilityHigh", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawBoundaries */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw boundaries"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawBoundaries"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawBoundaries}
                        onChange={(event) => {
                          setFieldValue("nDrawBoundaries", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawExtrema */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw extrema"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawExtrema"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawExtrema}
                        onChange={(event) => {
                          setFieldValue("nDrawExtrema", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawSupRes */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw supports and resistances"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawSupRes"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawSupRes}
                        onChange={(event) => {
                          setFieldValue("nDrawSupRes", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawSwingPhase */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw swing phase"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawSwingPhase"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nDrawSwingPhase}
                        onChange={(event) => {
                          setFieldValue("nDrawSwingPhase", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* strSupResForward */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Supports and resistances forward"
                          helpReference="HandleChartAnalyzers"
                          helpText=""
                          helpLink=""
                          fieldName="strSupResForward"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartTextInput textFieldName="strSupResForward" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dSupportMultiplier */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Support multiplier"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dSupportMultiplier"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dSupportMultiplier" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dResistanceMultiplier */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Resistance multiplier"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dResistanceMultiplier"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dResistanceMultiplier" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    </>
                  ) : null}

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Help Sections */}
                      {/* ---------------------------------------------------------------------- */}
                      {CHART_ANALYZER_REGRESSION_GRADIENT_HELP_IMAGE && (
                        <Box>
                          <B3Tiny
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setCollapseRegressionGradient(!collapseRegressionGradient);
                            }}
                          >
                            {collapseRegressionGradient ? "Hide help section" : "Show help section"}
                          </B3Tiny>

                          <Collapse sx={{ p: "3px" }} in={collapseRegressionGradient} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1 }}>
                              <Typography variant="contentMiddle" sx={{ my: 0.5 }}>
                                {CHART_ANALYZER_REGRESSION_GRADIENT_HELP_DESCRIPTION}
                                <br />
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={CHART_ANALYZER_REGRESSION_GRADIENT_HELP_IMAGE}
                                alt="Regression gradient"
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: "357px",
                                }}
                              />
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dRegressionAnglesMin */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Regression gradient minimum"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dRegressionAnglesMin"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dRegressionAnglesMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dRegressionAnglesMax */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Regression gradient maximum"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="dRegressionAnglesMax"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="dRegressionAnglesMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {CHART_ANALYZER_SWING_PHASE_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseSwingPhase(!collapseSwingPhase);
                        }}
                      >
                        {collapseSwingPhase ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseSwingPhase} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {CHART_ANALYZER_SWING_PHASE_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={CHART_ANALYZER_SWING_PHASE_HELP_IMAGE}
                            alt="Swing phases"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "559px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dBreakoutFactor */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Breakout factor"
                      helpReference="HandleChartAnalyzers"
                      helpText=""
                      helpLink=""
                      fieldName="dBreakoutFactor"
                      touched={touched}
                      errors={errors}
                    />
                    <SmartNumberInput textFieldName="dBreakoutFactor" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseOverstatement */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is overstatement"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseOverstatement"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseOverstatement}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseOverstatement", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseUnderstatement */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is understatement"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseUnderstatement"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseUnderstatement}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseUnderstatement", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseOverCorrection */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is over correction"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseOverCorrection"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseOverCorrection}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseOverCorrection", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseUnderCorrection */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is under correction"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseUnderCorrection"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseUnderCorrection}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseUnderCorrection", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseUpswing */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is upswing"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseUpswing"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseUpswing}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseUpswing", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseDownswing */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is downswing"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseDownswing"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseDownswing}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseDownswing", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTradeSwingPhaseUndefined */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Signal if swing phase is undefined"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="nTradeSwingPhaseUndefined"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ mt: "12px" }}
                        checked={values.nTradeSwingPhaseUndefined}
                        onChange={(event) => {
                          setFieldValue("nTradeSwingPhaseUndefined", event.target.checked);
                        }}
                      />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {CHART_ANALYZER_CURRENT_SWING_GRADIENT_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseCurrentSwingGradient(!collapseCurrentSwingGradient);
                        }}
                      >
                        {collapseCurrentSwingGradient ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseCurrentSwingGradient} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {CHART_ANALYZER_CURRENT_SWING_GRADIENT_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={CHART_ANALYZER_CURRENT_SWING_GRADIENT_HELP_IMAGE}
                            alt="Current swing gradient"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "357px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dCurrentSwingAngleMin */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Current swing gradient minimum"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="dCurrentSwingAngleMin"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="dCurrentSwingAngleMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dCurrentSwingAngleMax */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Current swing gradient maximum"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="dCurrentSwingAngleMax"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="dCurrentSwingAngleMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {CHART_ANALYZER_CURRENT_SWING_POSITION_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseCurrentSwingPosition(!collapseCurrentSwingPosition);
                        }}
                      >
                        {collapseCurrentSwingPosition ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseCurrentSwingPosition} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {CHART_ANALYZER_CURRENT_SWING_POSITION_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={CHART_ANALYZER_CURRENT_SWING_POSITION_HELP_IMAGE}
                            alt="Current swing position"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "357px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dCurrentSwingPosMin */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Current swing position minimum"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="dCurrentSwingPosMin"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="dCurrentSwingPosMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dCurrentSwingPosMax */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Current swing position maximum"
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="dCurrentSwingPosMax"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="dCurrentSwingPosMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>
                  </Box>

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nValueAnalysisFeedbackState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Value analysis feedback state"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="nValueAnalysisFeedbackState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nValueAnalysisFeedbackState}
                            onChange={(event) => {
                              setFieldValue("nValueAnalysisFeedbackState", event.target.checked);
                            }}
                          />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nSignalAnalysisFeedbackState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Signal analysis feedback state"
                            helpReference="HandleChartAnalyzers"
                            helpText=""
                            helpLink=""
                            fieldName="nSignalAnalysisFeedbackState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nSignalAnalysisFeedbackState}
                            onChange={(event) => {
                              setFieldValue("nSignalAnalysisFeedbackState", event.target.checked);
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Swing Configurations */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Box sx={{ mb: 1 }}>
                      <SmartParameter
                        value={`Swing configurations (${swingConfigurations.length}/${MAX_SWING_CONFIGURATIONS})`}
                        helpReference="HandleChartAnalyzers"
                        helpText=""
                        helpLink=""
                        fieldName="Swing configurations"
                      />
                    </Box>

                    <Stack spacing={2}>
                      {swingConfigurations.length < MAX_SWING_CONFIGURATIONS && (
                        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={addNewSwingConfig}>
                          Add new swing configuration
                        </B1Tiny>
                      )}

                      {swingConfigurations.map((swing) => (
                        <Box
                          key={swing.id}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            p: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                          }}
                        >
                          <Stack spacing={2}>
                            {/* Remove Button */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Box>
                                <Typography variant="contentBig" align="center">
                                  Swing {swing.id}
                                </Typography>
                              </Box>
                              <IconButton size="small" onClick={() => removeSwingConfig(swing.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Help Sections */}
                            {/* ---------------------------------------------------------------------- */}
                            {CHART_ANALYZER_SWING_NUMBER_HELP_IMAGE && (
                              <Box>
                                <B3Tiny
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    setCollapseSwingNumber(!collapseSwingNumber);
                                  }}
                                >
                                  {collapseSwingNumber ? "Hide help section" : "Show help section"}
                                </B3Tiny>

                                <Collapse sx={{ p: "3px" }} in={collapseSwingNumber} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1 }}>
                                    <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                      {CHART_ANALYZER_SWING_NUMBER_HELP_DESCRIPTION}
                                      <br />
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      borderRadius: "10px",
                                      py: 2,
                                      boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    <CardMedia
                                      component="img"
                                      image={CHART_ANALYZER_SWING_NUMBER_HELP_IMAGE}
                                      alt="Swing number"
                                      sx={{
                                        objectFit: "contain",
                                        maxHeight: "357px",
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            )}

                            {/* Swing Number and Enable Switch */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Swing number"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_nNumber`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  limits={DEFAULT_LIMITS.SWING_nNumber}
                                  value={swing.nNumber}
                                  onChange={(value) => updateSwingConfig(swing.id, "nNumber", value)}
                                  error={Boolean(swingErrors[swing.id]?.nNumber)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Swing enabled"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName="Swing enabled"
                                />
                                <ModalContentSwitch
                                  sx={{ mt: "12px" }}
                                  checked={swing.bState}
                                  onChange={(e) => updateSwingConfig(swing.id, "bState", e.target.checked)}
                                />
                              </Box>
                            </Box>

                            {/* Divider */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Help Sections */}
                            {/* ---------------------------------------------------------------------- */}
                            {CHART_ANALYZER_SWING_WIDTH_HEIGHT_HELP_IMAGE && (
                              <Box>
                                <B3Tiny
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    setCollapseSwingWidthHeight(!collapseSwingWidthHeight);
                                  }}
                                >
                                  {collapseSwingWidthHeight ? "Hide help section" : "Show help section"}
                                </B3Tiny>

                                <Collapse sx={{ p: "3px" }} in={collapseSwingWidthHeight} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1 }}>
                                    <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                      {CHART_ANALYZER_SWING_WIDTH_HEIGHT_HELP_DESCRIPTION}
                                      <br />
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      borderRadius: "10px",
                                      py: 2,
                                      boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    <CardMedia
                                      component="img"
                                      image={CHART_ANALYZER_SWING_WIDTH_HEIGHT_HELP_IMAGE}
                                      alt="Swing width / height"
                                      sx={{
                                        objectFit: "contain",
                                        maxHeight: "357px",
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            )}

                            {/* Height Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Height minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_heightMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.heightMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "heightMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dHeight}
                                  error={Boolean(swingErrors[swing.id]?.heightMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Height maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_heightMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.heightMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "heightMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dHeight}
                                  error={Boolean(swingErrors[swing.id]?.heightMax)}
                                />
                              </Box>
                            </Box>

                            {/* Width Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Width minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_widthMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.widthMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "widthMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dWidth}
                                  error={Boolean(swingErrors[swing.id]?.widthMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Width maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_widthMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.widthMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "widthMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dWidth}
                                  error={Boolean(swingErrors[swing.id]?.widthMax)}
                                />
                              </Box>
                            </Box>

                            {/* Divider */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Help Sections */}
                            {/* ---------------------------------------------------------------------- */}
                            {CHART_ANALYZER_SWING_RANGE_WIDTH_RANGE_HEIGHT_HELP_IMAGE && (
                              <Box>
                                <B3Tiny
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    setCollapseSwingRangeWidthRangeHeight(!collapseSwingRangeWidthRangeHeight);
                                  }}
                                >
                                  {collapseSwingRangeWidthRangeHeight ? "Hide help section" : "Show help section"}
                                </B3Tiny>

                                <Collapse sx={{ p: "3px" }} in={collapseSwingRangeWidthRangeHeight} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1 }}>
                                    <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                      {CHART_ANALYZER_SWING_RANGE_WIDTH_RANGE_HEIGHT_HELP_DESCRIPTION}
                                      <br />
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      borderRadius: "10px",
                                      py: 2,
                                      boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    <CardMedia
                                      component="img"
                                      image={CHART_ANALYZER_SWING_RANGE_WIDTH_RANGE_HEIGHT_HELP_IMAGE}
                                      alt="Swing range width / range height"
                                      sx={{
                                        objectFit: "contain",
                                        maxHeight: "357px",
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            )}

                            {/* Range Height Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range height minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeHeightMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeHeightMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeHeightMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeHeightMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range height maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeHeightMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeHeightMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeHeightMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeHeightMax)}
                                />
                              </Box>
                            </Box>

                            {/* Range Width Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range width minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeWidthMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeWidthMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeWidthMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeWidthMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range width maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeWidthMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeWidthMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeWidthMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeWidthMax)}
                                />
                              </Box>
                            </Box>

                            {/* Divider */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Help Sections */}
                            {/* ---------------------------------------------------------------------- */}
                            {CHART_ANALYZER_SWING_POINT_POSITION_HELP_IMAGE && (
                              <Box>
                                <B3Tiny
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => {
                                    setCollapseSwingPointPosition(!collapseSwingPointPosition);
                                  }}
                                >
                                  {collapseSwingPointPosition ? "Hide help section" : "Show help section"}
                                </B3Tiny>

                                <Collapse sx={{ p: "3px" }} in={collapseSwingPointPosition} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1 }}>
                                    <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                      {CHART_ANALYZER_SWING_POINT_POSITION_HELP_HELP_DESCRIPTION}
                                      <br />
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      borderRadius: "10px",
                                      py: 2,
                                      boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    <CardMedia
                                      component="img"
                                      image={CHART_ANALYZER_SWING_POINT_POSITION_HELP_IMAGE}
                                      alt="Swing point position"
                                      sx={{
                                        objectFit: "contain",
                                        maxHeight: "357px",
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            )}

                            {/* Range Upper Point Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range upper point minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeUpperPointMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeUpperPointMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeUpperPointMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeUpperPointMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range upper point maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeUpperPointMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeUpperPointMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeUpperPointMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeUpperPointMax)}
                                />
                              </Box>
                            </Box>

                            {/* Range Lower Point Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range lower point minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeLowerPointMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeLowerPointMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeLowerPointMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeLowerPointMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range lower point maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeLowerPointMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeLowerPointMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeLowerPointMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeLowerPointMax)}
                                />
                              </Box>
                            </Box>

                            {/* Range Left Point Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range left point minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeLeftPointMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeLeftPointMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeLeftPointMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeLeftPointMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range left point maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeLeftPointMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeLeftPointMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeLeftPointMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeLeftPointMax)}
                                />
                              </Box>
                            </Box>

                            {/* Range Right Point Parameters */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <SmartParameter
                                  value="Range right point minimum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeRightPointMin`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeRightPointMin}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeRightPointMin", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeRightPointMin)}
                                />
                              </Box>
                              <Box>
                                <SmartParameter
                                  value="Range right point maximum"
                                  helpReference="HandleChartAnalyzers"
                                  helpText=""
                                  helpLink=""
                                  fieldName={`swing_rangeRightPointMax`}
                                  touched={swingFormikStructure.touched}
                                  errors={swingFormikStructure.errors}
                                />
                                <SmartSwingNumberInput
                                  value={swing.rangeRightPointMax}
                                  onChange={(value) => updateSwingConfig(swing.id, "rangeRightPointMax", value)}
                                  limits={DEFAULT_LIMITS.SWING_dRange}
                                  error={Boolean(swingErrors[swing.id]?.rangeRightPointMax)}
                                />
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
